import CircleByIdButton from '@/circle/components/CircleByIdButton'
import CircleMemberLink from '@/circle/components/CircleMemberLink'
import ActionsMenu from '@/common/atoms/actionsMenu/ActionsMenu'
import CopyLinkMenuItem from '@/common/atoms/actionsMenu/CopyLinkMenuItem'
import DeleteMenuItem from '@/common/atoms/actionsMenu/DeleteMenuItem'
import Loading from '@/common/atoms/Loading'
import Switch from '@/common/atoms/Switch'
import { Title } from '@/common/atoms/Title'
import { usePreventClose } from '@/common/hooks/usePreventClose'
import Page404 from '@/common/pages/Page404'
import CollabEditor from '@/editor/components/CollabEditor'
import EditorController from '@/editor/components/EditorController'
import MeetingOriginBanner from '@/meeting/components/MeetingOriginBanner'
import MemberByIdButton from '@/member/components/MemberByIdButton'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import useOrgMember from '@/member/hooks/useOrgMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { usePathInOrg } from '@/org/hooks/usePathInOrg'
import CircleSearchInput from '@/search/components/CircleSearchInput'
import MemberSearchInput from '@/search/components/MemberSearchInput'
import ThreadItem from '@/thread/components/ThreadItem'
import VisibilityField from '@/visibility/components/VisibilityField'
import VisibilityIcon from '@/visibility/components/VisibilityIcon'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  BoxProps,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spacer,
  Tag,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import {
  Task_Status_Enum,
  TaskFragment,
  useTaskSubscription,
  useTaskThreadsQuery,
  useUpdateTaskMutation,
  Visibility_Enum,
} from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  defaultVisibility,
  getVisibilityAccess,
} from '@rolebase/shared/model/visibility'
import { nameSchema } from '@rolebase/shared/schemas'
import { getDateTimeLocal } from '@utils/dates'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import settings from 'src/settings'
import * as yup from 'yup'
import useAutoProposeMember from '../hooks/useAutoProposeMember'
import useCreateTask from '../hooks/useCreateTask'
import useUpdateTaskStatus from '../hooks/useUpdateTaskStatus'
import TaskDeleteModal from '../modals/TaskDeleteModal'
import TaskMemberProposalHint from './TaskMemberProposalHint'
import TaskStatusInput from './TaskStatusInput'
import TaskStatusTag from './TaskStatusTag'

interface Props extends BoxProps {
  id?: string
  // #123: the card version filled in a tension during a meeting — title, role,
  // member, description and due date; Circle visibility, set afterwards from
  // the project if needed; a Cancel button beside Create.
  compact?: boolean
  changeTitle?: boolean
  headerIcons?: React.ReactNode
  defaults?: Partial<TaskFragment>
  onClose(): void
  onCreate?(taskId: string): void
}

interface Values {
  circleId: string
  memberId: string | null
  title: string
  description: string
  dueDate: string | null
  visibility: Visibility_Enum
}

const resolver = yupResolver(
  yup.object().shape({
    title: nameSchema.required(),
    circleId: yup.string().required(),
    description: yup.string(),
  })
)

export default function TaskContent({
  id,
  compact,
  changeTitle,
  defaults,
  headerIcons,
  onClose,
  onCreate,
  ...boxProps
}: Props) {
  const { t } = useTranslation()
  const { orgId, orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const updateTaskStatus = useUpdateTaskStatus()
  const createTask = useCreateTask()
  const [updateTask] = useUpdateTaskMutation()
  const { preventClose, allowClose } = usePreventClose()

  const { data, loading, error } = useTaskSubscription({
    skip: !id,
    variables: {
      id: id!,
    },
  })
  const task = data?.task_by_pk
  const path = usePathInOrg(`tasks/${id}`)

  // Threads that reference this task (in a thread activity)
  const { data: threadsData } = useTaskThreadsQuery({
    skip: !id,
    variables: { taskId: id! },
  })
  const threadActivities = threadsData?.thread_activity

  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<Values>({
    resolver,
    defaultValues: {
      circleId: defaults?.circleId || '',
      memberId: defaults?.memberId || null,
      title: defaults?.title || '',
      description: defaults?.description || '',
      dueDate: null,
      visibility: defaults?.visibility ?? defaultVisibility.task,
    },
  })

  // Reset form when task changes
  useEffect(() => {
    if (!task) return
    reset({
      circleId: task.circleId,
      memberId: task.memberId || null,
      title: task.title,
      description: task.description,
      dueDate: task.dueDate ? getDateTimeLocal(new Date(task.dueDate)) : null,
      visibility: task.visibility,
    })
  }, [task])

  const dueDate = watch('dueDate')

  const onSubmit = handleSubmit(async ({ dueDate, memberId, ...data }) => {
    if (!orgId || !currentMember) return
    const taskUpdate = {
      ...data,
      memberId: memberId ?? null,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
    }
    if (id) {
      // Update task
      await updateTask({
        variables: { id, values: taskUpdate },
      })
      allowClose()
    } else {
      // Create task
      const newTask = await createTask({
        orgId,
        status: defaults?.status || Task_Status_Enum.Open,
        ...taskUpdate,
      })
      if (!newTask) return

      onCreate?.(newTask.id)
      onClose()
    }
  })

  // Save after X seconds
  const watchedData = watch()

  // #123: a role held by one person proposes that person as the member
  const { proposal: memberProposal, isProposed: isMemberProposed } =
    useAutoProposeMember({
      enabled: !id,
      circleId: watchedData.circleId || undefined,
      memberId: watchedData.memberId,
      isMemberDirty: !!dirtyFields.memberId,
      setMemberId: (memberId) => setValue('memberId', memberId),
    })
  const onSubmitDebounced = useMemo(
    () => debounce(onSubmit, 1000),
    [id, orgId, currentMember]
  )
  useEffect(() => {
    if (!id || !isDirty || !isVisibilityAllowed) return
    preventClose()
    onSubmitDebounced()
  }, [id, isDirty, ...Object.values(watchedData)])

  // Visibility (CONF-02). Writing follows the level SAVED on the task, not the
  // one being edited: the Org level gives reading, not writing (D8). The level
  // being chosen must still leave the author able to modify what they save.
  // Display rule only — the Hasura permission decides.
  const isOrgWriter = useOrgMember()
  const accessOf = (
    visibility: Visibility_Enum,
    circleId: string,
    assigneeId: string | null | undefined
  ) =>
    orgData && circleId
      ? getVisibilityAccess({
          orgData,
          circleId,
          visibility,
          memberId: currentMember?.id,
          namedMemberIds: assigneeId ? [assigneeId] : [],
          isOrgWriter,
        })
      : { canRead: true, canWrite: isOrgWriter }
  // The task as saved decides whether the form is editable at all…
  const isMember = task
    ? accessOf(task.visibility, task.circleId, task.memberId).canWrite
    : isOrgWriter
  // …the values being edited decide whether they can be saved.
  const isVisibilityAllowed = accessOf(
    watchedData.visibility,
    watchedData.circleId,
    watchedData.memberId
  ).canWrite

  // Toggle due date
  const handleToggleDueDate = useCallback(() => {
    if (dueDate) {
      setValue('dueDate', null, { shouldDirty: true })
    } else {
      const date = new Date()
      date.setDate(date.getDate() + 1)
      date.setHours(12, 0, 0, 0)
      setValue('dueDate', getDateTimeLocal(date), { shouldDirty: true })
    }
  }, [dueDate])

  // Change task status
  const handleChangeStatus = useCallback(
    (status: Task_Status_Enum) => {
      if (!task) return
      updateTaskStatus(task, status)
    },
    [task, updateTaskStatus]
  )

  // Change description
  const handleDescriptionChange = useCallback(
    async (description: string) => {
      setValue('description', description, { shouldDirty: true })
    },
    [task]
  )

  // Task deletion modal
  const deleteModal = useDisclosure()

  if (error || (id && !task && !loading)) {
    console.error(error || new Error('Task not found'))
    return <Page404 />
  }

  return (
    <Box {...boxProps}>
      {changeTitle && <Title>{task?.title || '…'}</Title>}

      {!id && !compact && <MeetingOriginBanner mb={4} />}

      <Flex align="center" mb={compact ? 3 : 5}>
        <Heading as={compact ? 'h3' : 'h1'} size={compact ? 'sm' : 'md'}>
          {t(id ? 'TaskContent.headingEdit' : 'TaskContent.headingCreate')}
        </Heading>

        {task &&
          (isMember ? (
            <TaskStatusInput
              value={task.status}
              onChange={handleChangeStatus}
              ml={5}
              size="lg"
            />
          ) : (
            <TaskStatusTag status={task.status} ml={5} size="lg" />
          ))}

        {id && loading && <Loading active size="sm" ml={5} />}

        {task?.archivedAt && <Tag ml={2}>{t('common.archived')}</Tag>}

        <Spacer />

        {task && (
          <VisibilityIcon
            objectType="task"
            circleId={task.circleId}
            level={task.visibility}
            size={20}
            mr={2}
          />
        )}

        <Flex mr={headerIcons ? -3 : 0}>
          {id && isMember && (
            <ActionsMenu ml={3}>
              <CopyLinkMenuItem url={`${settings.url}${path}`} />
              <DeleteMenuItem onClick={deleteModal.onOpen} />
            </ActionsMenu>
          )}

          {headerIcons}
        </Flex>
      </Flex>

      <VStack spacing={compact ? 5 : 10} alignItems="start" mb={3}>
        <FormControl isInvalid={!!errors.title}>
          <Input
            {...register('title')}
            size={compact ? 'md' : 'lg'}
            placeholder={t('TaskContent.titlePlaceholder')}
            autoFocus
            readOnly={!isMember}
          />
        </FormControl>

        <FormControl isInvalid={!!errors.description}>
          <FormLabel>{t('TaskContent.description')}</FormLabel>
          {task ? (
            <CollabEditor
              docId={`task-${id}-description`}
              value={task.description}
              placeholder={t('TaskContent.notes')}
              minH="80px"
              saveEvery={10000}
              readOnly={!isMember}
              onSave={handleDescriptionChange}
            />
          ) : (
            <EditorController
              name="description"
              placeholder={t('TaskContent.notes')}
              control={control}
            />
          )}
        </FormControl>

        <Flex flexWrap="wrap">
          <FormControl isInvalid={!!errors.circleId} w="auto" mr={5}>
            <FormLabel>{t('TaskContent.circle')}</FormLabel>
            <Controller
              name="circleId"
              control={control}
              render={({ field }) =>
                isMember ? (
                  <CircleSearchInput
                    defaultPerimeter="myRoles"
                    disableOutsidePerimeter
                    writableVisibility={watchedData.visibility}
                    value={field.value}
                    onChange={field.onChange}
                  />
                ) : (
                  <CircleByIdButton id={field.value} />
                )
              }
            />
          </FormControl>

          <FormControl isInvalid={!!errors.memberId} w="auto">
            <FormLabel>{t('TaskContent.memberId')}</FormLabel>
            <Controller
              name="memberId"
              control={control}
              render={({ field }) =>
                isMember ? (
                  <MemberSearchInput
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    onClear={() => field.onChange(null)}
                  />
                ) : field.value ? (
                  <CircleMemberLink memberId={field.value}>
                    <MemberByIdButton id={field.value} size="sm" />
                  </CircleMemberLink>
                ) : (
                  <></>
                )
              }
            />
            <TaskMemberProposalHint
              proposal={memberProposal}
              isProposed={isMemberProposed}
              hasMember={!!watchedData.memberId}
            />
          </FormControl>
        </Flex>

        <VStack spacing={3} align="start">
          <FormControl>
            <Switch
              isChecked={!!dueDate}
              isReadOnly={!isMember}
              onChange={handleToggleDueDate}
            >
              {t('TaskContent.dueDate')}
            </Switch>
            <Collapse in={!!dueDate}>
              <Box mt={4} mb={2} pl={10}>
                <Input
                  {...register('dueDate')}
                  type="datetime-local"
                  w="250px"
                  readOnly={!isMember}
                />
              </Box>
            </Collapse>
          </FormControl>

          {!compact && (
            <Controller
              name="visibility"
              control={control}
              render={({ field }) => (
                <VisibilityField
                  objectType="task"
                  circleId={watchedData.circleId}
                  value={field.value}
                  onChange={field.onChange}
                  isReadOnly={!isMember}
                />
              )}
            />
          )}
        </VStack>

        <Collapse in={!isVisibilityAllowed}>
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              {t('VisibilityField.notAllowed')}
            </AlertDescription>
          </Alert>
        </Collapse>

        {threadActivities && threadActivities.length > 0 && (
          <VStack align="stretch" spacing={1} w="100%">
            {threadActivities.map(
              (activity) =>
                activity.thread && (
                  <ThreadItem
                    key={activity.id}
                    thread={activity.thread}
                    showIcon
                    showCircle
                    openButton
                  />
                )
            )}
          </VStack>
        )}

        {!id && isMember && (
          <Box w="100%" textAlign="right">
            {compact && (
              <Button variant="ghost" mr={2} onClick={onClose}>
                {t('common.cancel')}
              </Button>
            )}
            <Button
              colorScheme="blue"
              isDisabled={!isVisibilityAllowed}
              onClick={onSubmit}
            >
              {t('common.create')}
            </Button>
          </Box>
        )}
      </VStack>

      {deleteModal.isOpen && task && (
        <TaskDeleteModal
          task={task}
          isOpen
          onClose={deleteModal.onClose}
          onDelete={onClose}
        />
      )}
    </Box>
  )
}
